import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Download,
  Eye,
  List,
  RotateCcw,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SortableTableHead } from "../components/SortableTableHead";
import { StatusBadge } from "../components/StatusBadge";
import { inputClass, selectClass } from "../components/FormField";
import { TablePagination } from "../components/TablePagination";
import { useEnterpriseBeneficiaries } from "../hooks/useEnterpriseBeneficiaries";
import type { Beneficiary } from "../types";

function getBeneficiaryDetailPath(beneficiary: Beneficiary): string | null {
  if (beneficiary.recordId && beneficiary.beneficiaryType) {
    return `/dashboard/beneficiaries/${beneficiary.beneficiaryType}/${beneficiary.recordId}`;
  }

  const match = beneficiary.id.match(/^(emp|fm)-(\d+)$/);
  if (!match) return null;

  const type = match[1] === "emp" ? "employee" : "dependant";
  return `/dashboard/beneficiaries/${type}/${match[2]}`;
}

function getInitials(name: string) {
  return name
    .replace(/^\d+\.\s*/, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function EnterpriseBeneficiariesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.beneficiaries.enterpriseList;
  const pageTitle = t.safecheck.nav.beneficiaryList;
  const { exportTable } = usePageActions();
  const { beneficiaries, loading, error } = useEnterpriseBeneficiaries();

  const [topSearch, setTopSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const { sortKey, sortDirection, toggleSort } = useTableSort("name", "asc");

  const handleSort = (key: string) => {
    toggleSort(key);
    setPage(1);
  };

  const resetFilters = () => {
    setTopSearch("");
    setTableSearch("");
    setPage(1);
  };

  const filtered = useMemo(() => {
    return beneficiaries.filter((beneficiary) => {
      const haystack =
        `${beneficiary.name} ${beneficiary.matricule} ${beneficiary.companyMatricule ?? ""} ${beneficiary.relation} ${beneficiary.company}`.toLowerCase();
      const byTopSearch = topSearch ? haystack.includes(topSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      return byTopSearch && byTableSearch;
    });
  }, [beneficiaries, topSearch, tableSearch]);

  const sorted = useMemo(
    () =>
      sortByColumn(filtered, sortKey, sortDirection, (beneficiary, key) => {
        if (key === "name") return beneficiary.name.replace(/^\d+\.\s*/, "");
        if (key === "relation") return beneficiary.relation;
        if (key === "systemId") return beneficiary.matricule;
        if (key === "companyMatricule") return beneficiary.companyMatricule ?? "";
        if (key === "company") return beneficiary.company;
        return "";
      }),
    [filtered, sortKey, sortDirection],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const columnDefs = [
    { key: "photo", label: labels.columns.photo, sortable: false },
    { key: "name", label: labels.columns.name, sortable: true },
    { key: "relation", label: labels.columns.relation, sortable: true },
    { key: "systemId", label: labels.columns.systemId, sortable: true },
    { key: "companyMatricule", label: labels.columns.companyMatricule, sortable: true },
    { key: "company", label: labels.columns.company, sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ] as const;

  const handleExportCsv = () => {
    exportTable(
      "beneficiaires-ceec.csv",
      [
        labels.columns.name,
        labels.columns.relation,
        labels.columns.systemId,
        labels.columns.companyMatricule,
        labels.columns.company,
      ],
      sorted.map((beneficiary) => [
        beneficiary.name,
        beneficiary.relation,
        beneficiary.matricule,
        beneficiary.companyMatricule ?? "",
        beneficiary.company,
      ]),
    );
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Breadcrumbs
          homeHref="/dashboard/enterprise"
          items={[
            { label: t.safecheck.nav.employeeManagement },
            { label: pageTitle },
          ]}
        />
        <Link
          to="/dashboard/employees"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <List size={16} />
          {labels.backToEmployees}
        </Link>
      </div>

      {error && (
        <p className="mb-3 rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}
      {loading && <p className="mb-3 text-sm text-muted">Chargement des bénéficiaires…</p>}

      <div className="theme-card">
        <div className="border-b border-border px-5 py-4">
          <h1 className="text-base font-semibold text-foreground">{pageTitle}</h1>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <input
            value={topSearch}
            onChange={(event) => {
              setTopSearch(event.target.value);
              setPage(1);
            }}
            placeholder={labels.searchPlaceholder}
            className={`${inputClass} w-full min-w-[200px] max-w-sm`}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExportCsv}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
            >
              <Download size={15} />
              {labels.exportCsv}
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
              aria-label={t.safecheck.reset}
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex items-center gap-2 text-sm text-muted">
            <span>{t.safecheck.show}</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className={selectClass}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>{t.safecheck.entries}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted">{t.safecheck.searchLabel}</span>
            <input
              value={tableSearch}
              onChange={(event) => {
                setTableSearch(event.target.value);
                setPage(1);
              }}
              className="h-8 rounded-md border border-border bg-card px-3 text-sm outline-none focus:border-primary"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((beneficiary) => {
                const detailPath = getBeneficiaryDetailPath(beneficiary);
                return (
                <tr
                  key={beneficiary.id}
                  className="border-b border-border last:border-0 hover:bg-card-muted/30"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <ChevronRight size={14} className="text-muted" />
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-purple-soft text-xs font-semibold text-purple">
                        {getInitials(beneficiary.name)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-foreground">
                    {detailPath ? (
                      <Link
                        to={detailPath}
                        className="text-primary transition-colors hover:text-primary/80"
                      >
                        {beneficiary.name}
                      </Link>
                    ) : (
                      beneficiary.name
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge variant="purple">{beneficiary.relation}</StatusBadge>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge variant="purple">{beneficiary.matricule}</StatusBadge>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {beneficiary.companyMatricule}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{beneficiary.company}</td>
                  <td className="px-4 py-3">
                    {detailPath ? (
                      <Link
                        to={detailPath}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-card-muted"
                      >
                        <Eye size={14} />
                        Voir
                      </Link>
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                </tr>
              );
              })}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={sorted.length}
          onPageChange={handlePageChange}
          labels={labels.pagination}
        />
      </div>
    </>
  );
}
